import { Box } from "@mui/material";
import { FC, useEffect, useState } from "react";
import {
  VETCOIN_ANIMATION_EVENT,
  VETCOIN_BALANCE_TARGET_ID,
  VetcoinAnimationDetail,
  triggerVetcoinAnimationComplete,
} from "utils/vetcoinAnimation";

type FlyingCoin = {
  id: number;
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  delayMs: number;
  size: number;
  driftX: number;
  bounceX: number;
};

const COIN_COUNT = 10;
const FLIGHT_MS = 2600;
const STAGGER_MS = 90;

const getBalanceTargetCenter = () => {
  const target = document.getElementById(VETCOIN_BALANCE_TARGET_ID);
  if (target) {
    const rect = target.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  }
  return {
    x: window.innerWidth - 96,
    y: 28,
  };
};

export const VetcoinFlyAnimation: FC = () => {
  const [coins, setCoins] = useState<FlyingCoin[]>([]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<VetcoinAnimationDetail>).detail;
      const amount = detail?.amount ?? 0;
      const count = Math.min(
        COIN_COUNT,
        Math.max(6, amount > 0 ? Math.ceil(amount / 2) : COIN_COUNT),
      );

      const startX = window.innerWidth / 2;
      const startY = window.innerHeight / 2;
      const target = getBalanceTargetCenter();

      const nextCoins: FlyingCoin[] = Array.from({ length: count }, (_, i) => ({
        id: Date.now() + i,
        startX: startX + (Math.random() - 0.5) * 56,
        startY: startY + (Math.random() - 0.5) * 40,
        endX: target.x + (Math.random() - 0.5) * 12,
        endY: target.y + (Math.random() - 0.5) * 8,
        delayMs: i * STAGGER_MS,
        size: 20 + Math.random() * 10,
        driftX: (Math.random() - 0.5) * 60,
        bounceX: (Math.random() - 0.5) * 36,
      }));

      setCoins(nextCoins);

      const pulseAt = (count - 1) * STAGGER_MS + FLIGHT_MS - 180;
      const clearAt = (count - 1) * STAGGER_MS + FLIGHT_MS + 280;

      window.setTimeout(() => {
        document
          .getElementById(VETCOIN_BALANCE_TARGET_ID)
          ?.classList.add("vetcoin-balance-pulse");
        triggerVetcoinAnimationComplete();
      }, pulseAt);

      window.setTimeout(() => {
        setCoins([]);
        document
          .getElementById(VETCOIN_BALANCE_TARGET_ID)
          ?.classList.remove("vetcoin-balance-pulse");
      }, clearAt);
    };

    window.addEventListener(VETCOIN_ANIMATION_EVENT, handler);
    return () => window.removeEventListener(VETCOIN_ANIMATION_EVENT, handler);
  }, []);

  if (!coins.length) return null;

  return (
    <Box
      aria-hidden
      sx={{
        pointerEvents: "none",
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        overflow: "hidden",
        "@keyframes vetcoinFly": {
          "0%": {
            transform: "translate(0px, 0px) scale(0.2)",
            opacity: 0,
          },
          "8%": {
            transform: "translate(var(--bounce-x), -8px) scale(1.2)",
            opacity: 1,
          },
          "18%": {
            transform: "translate(var(--bounce-x), 18px) scale(0.92)",
            opacity: 1,
          },
          "28%": {
            transform: "translate(var(--bounce-x), -28px) scale(1.12)",
            opacity: 1,
          },
          "38%": {
            transform: "translate(var(--bounce-x), 10px) scale(0.96)",
            opacity: 1,
          },
          "48%": {
            transform: "translate(var(--drift-x), -16px) scale(1.05)",
            opacity: 1,
          },
          "100%": {
            transform: "translate(var(--dx), var(--dy)) scale(0.3)",
            opacity: 0.1,
          },
        },
        "@keyframes vetcoinSpin": {
          "0%": { transform: "rotate(0deg)" },
          "48%": { transform: "rotate(180deg)" },
          "100%": { transform: "rotate(900deg)" },
        },
      }}
    >
      {coins.map((coin) => {
        const dx = coin.endX - coin.startX;
        const dy = coin.endY - coin.startY;
        return (
          <Box
            key={coin.id}
            sx={{
              position: "absolute",
              left: coin.startX,
              top: coin.startY,
              width: coin.size,
              height: coin.size,
              ml: `${-coin.size / 2}px`,
              mt: `${-coin.size / 2}px`,
              borderRadius: "50%",
              background:
                "radial-gradient(circle at 30% 30%, #ffe566 0%, #f5c518 45%, #d4a017 100%)",
              border: "2px solid #b8860b",
              boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#7a5a00",
              fontWeight: 800,
              fontSize: Math.max(9, coin.size * 0.45),
              lineHeight: 1,
              "--dx": `${dx}px`,
              "--dy": `${dy}px`,
              "--drift-x": `${coin.driftX}px`,
              "--bounce-x": `${coin.bounceX}px`,
              animation: `vetcoinFly ${FLIGHT_MS}ms cubic-bezier(0.33, 0.1, 0.25, 1) forwards`,
              animationDelay: `${coin.delayMs}ms`,
              opacity: 0,
              "& > span": {
                animation: `vetcoinSpin ${FLIGHT_MS}ms ease-in-out forwards`,
                animationDelay: `${coin.delayMs}ms`,
                display: "inline-block",
              },
            }}
          >
            <span>V</span>
          </Box>
        );
      })}
    </Box>
  );
};
